/* eslint-disable eqeqeq */
import { Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, RouterStateSnapshot, Router } from '@angular/router';
import { Observable } from 'rxjs';
import { DynamicGuardService } from '@workflow/dynamic-guard/dynamic-guard.decorator';
import { Info } from '../constant/information.const';
import { CanComponentDeactivate } from '../model/can-component-deactivate.type';
import { StorageWrapperService } from '../service/storage-wrapper/storage-wrapper.service';
import { UserService } from '../service/user/user.service';

@DynamicGuardService('PreventApplicatinBackGuard')
@Injectable({ providedIn: 'root' })
export class PreventApplicatinBackGuard {
  constructor(
    private readonly userService: UserService,
    private readonly router: Router,
    private readonly storageWrapperService: StorageWrapperService
  ) {}

  /**
   * Guard hook that determines if a route can be deactivated.
   * Prevents authenticated users with OAO_PostApp allowed route from navigating back to OAO application flows.
   *
   * @param component - The component being deactivated
   * @param route - The current route snapshot
   * @param currentState - The current router state
   * @param nextState - The next router state containing the target URL
   * @returns True if navigation is allowed, false if blocked
   */
  public canDeactivate(
    component: CanComponentDeactivate,
    route: ActivatedRouteSnapshot,
    currentState: RouterStateSnapshot,
    nextState: RouterStateSnapshot
  ): Observable<boolean> | Promise<boolean> | boolean {
    return this.isAllowedPath(nextState.url);
  }

  /**
   * Guard hook that determines if a route can be activated.
   * Prevents authenticated users with OAO_PostApp allowed route from accessing OAO application flows.
   *
   * @param route - The activated route snapshot
   * @param state - The router state containing the current URL
   * @param next - The next activated route snapshot (unused)
   * @returns True if navigation is allowed, false if blocked
   */
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  public canActivate(route: ActivatedRouteSnapshot, state: RouterStateSnapshot, next: ActivatedRouteSnapshot): boolean {
    return this.isAllowedPath(state.url);
  }

  /**
   * Checks if the given URL path is allowed for the current user.
   * For authenticated users, restricts access to OAO flows if the allowed route is OAO_PostApp.
   * Redirects to dashboard if access is denied.
   *
   * @param url - The URL to check for access
   * @returns True if access is allowed, false if blocked
   */
  private isAllowedPath(url: string): boolean {
    const isAuthenticated = this.userService.isAuthenticated;

    if (isAuthenticated && this.isRestrictRouteForLoginUser(url)) {
      this.router.navigateByUrl(`/consumer/dashboard?return=dashboard`);
      return false;
    }
    return true;
  }

  /**
   * Determines if the URL should be restricted for logged-in users.
   * Checks if the allowed route is OAO_PostApp and the URL is an OAO flow,
   * or if the user is authenticated and trying to access verify page.
   *
   * @param url - The URL to check for restrictions
   * @returns True if the route should be restricted, false otherwise
   */
  private isRestrictRouteForLoginUser(url: string): boolean {
    const allowedRoute = this.storageWrapperService.get(Info.allowedRoute);
    if (allowedRoute === 'OAO_PostApp' && (url.includes('oao/') || url.includes('user/verify'))) {
      return true;
    }
    return false;
  }
}
